import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, Client } from '../services/authService';
import { chatService } from '../services/chatService';
import { deriveProfileComplete } from '../utils/profileCompletion';
import {
  clearPendingVerificationEmailStorage,
  getPendingVerificationEmailStorage,
  getVerificationEmailFromResponse,
  responseRequiresVerification,
  setPendingVerificationEmailStorage,
} from '../utils/pendingVerification';
import { clearMembershipStatus } from '../features/membership/membershipStatus';

interface AuthContextType {
  client: Client | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<any>;
  signUp: (data: {
    name: string;
    email: string;
    password: string;
    phone?: string;
    country?: string;
    state?: string;
  }) => Promise<any>;
  signInWithGoogle: (credential: string) => Promise<any>;
  logout: () => Promise<void>;
  refreshClient: () => Promise<void>;
  pendingVerificationEmail: string | null;
  setPendingVerificationEmail: (email: string) => void;
  clearPendingVerificationEmail: () => void;
  verifyOtpAndLogin: (email: string, otp: string) => Promise<Client>;
  verifyMagicLinkAndLogin: (token: string, email: string) => Promise<Client>;
  hasCompletedProfile: boolean;
  setHasCompletedProfile: (completed: boolean) => void;
  pendingProfileUser: { isNewSocialUser?: boolean } | null;
  setPendingProfileUser: (user: { isNewSocialUser?: boolean } | null) => void;
  clearPendingProfileUser: () => void;
  completeProfile: (phone: string, country: string, state: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [client, setClient] = useState<Client | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingVerificationEmail, setPendingVerificationEmailState] = useState<string | null>(
    () => getPendingVerificationEmailStorage()
  );
  const [hasCompletedProfile, setHasCompletedProfile] = useState<boolean>(false);
  const [pendingProfileUser, setPendingProfileUser] = useState<{ isNewSocialUser?: boolean } | null>(null);

  const setPendingVerificationEmail = (email: string) => {
    setPendingVerificationEmailStorage(email);
    setPendingVerificationEmailState(email);
  };
  const clearPendingVerificationEmail = () => {
    clearPendingVerificationEmailStorage();
    setPendingVerificationEmailState(null);
  };
  const clearPendingProfileUser = () => setPendingProfileUser(null);

  const checkAuth = async () => {
    try {
      if (authService.isAuthenticated()) {
        const clientData = await authService.getCurrentClient();
        setClient(clientData);
        setIsAuthenticated(true);
        setHasCompletedProfile(deriveProfileComplete(clientData));
        
        // Initialize socket connection for chat; pass client_id so the
        // backend auto-joins community group rooms on client:identify
        const token = authService.getToken();
        if (token) {
          chatService.initializeSocket(token, clientData.client_id);
        }
      } else {
        setClient(null);
        setIsAuthenticated(false);
        setHasCompletedProfile(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setClient(null);
      setIsAuthenticated(false);
      setHasCompletedProfile(false);
      authService.logout();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login({ email, password });
      const { client: clientData, token } = response;
      if (clientData) {
        setClient(clientData);
        setIsAuthenticated(true);
        setHasCompletedProfile(
          clientData.hasCompletedProfile ?? deriveProfileComplete(clientData)
        );

        if (token) {
          chatService.initializeSocket(token, clientData.client_id);
        }

        try {
          const fullClient = await authService.getCurrentClient();
          setClient(fullClient);
          setHasCompletedProfile(
            fullClient.hasCompletedProfile ?? deriveProfileComplete(fullClient)
          );
        } catch {
          // keep login response client
        }
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  const signUp = async (data: {
    name: string;
    email: string;
    password: string;
    phone?: string;
    country?: string;
    state?: string;
  }) => {
    try {
      const response = await authService.signUp(data);

      if (responseRequiresVerification(response)) {
        setPendingVerificationEmail(getVerificationEmailFromResponse(response, data.email));
        return response;
      }

      const { client: clientData, token } = response;
      if (clientData) {
        setClient(clientData);
        setIsAuthenticated(true);
        setHasCompletedProfile(deriveProfileComplete(clientData));

        if (token) {
          chatService.initializeSocket(token, clientData.client_id);
        }

        try {
          const fullClient = await authService.getCurrentClient();
          setClient(fullClient);
          setHasCompletedProfile(deriveProfileComplete(fullClient));
        } catch {
          // keep partial client from signup response
        }
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  const verifyOtpAndLogin = async (email: string, otp: string) => {
    try {
      const { client: clientData, token } = await authService.verifyOtp(email, otp);
      setClient(clientData);
      setIsAuthenticated(true);
      setHasCompletedProfile(deriveProfileComplete(clientData));
      clearPendingVerificationEmail();

      if (token) {
        chatService.initializeSocket(token, clientData.client_id);
      }

      try {
        const fullClient = await authService.getCurrentClient();
        setClient(fullClient);
        setHasCompletedProfile(deriveProfileComplete(fullClient));
        return fullClient;
      } catch {
        return clientData;
      }
    } catch (error) {
      throw error;
    }
  };

  const verifyMagicLinkAndLogin = async (token: string, email: string) => {
    try {
      const { client: clientData, token: sessionToken, hasCompletedProfile: hcp } =
        await authService.verifyMagicLink(token, email);
      setClient(clientData);
      setIsAuthenticated(true);
      setHasCompletedProfile(hcp ?? deriveProfileComplete(clientData));
      clearPendingVerificationEmail();

      if (sessionToken) {
        chatService.initializeSocket(sessionToken, clientData.client_id);
      }

      return clientData;
    } catch (error) {
      throw error;
    }
  };

  const signInWithGoogle = async (credential: string) => {
    try {
      const response = await authService.signInWithGoogle(credential);
      const { client: clientData, token, hasCompletedProfile: hcp } = response;
      setClient(clientData);
      setIsAuthenticated(true);
      setHasCompletedProfile(hcp ?? deriveProfileComplete(clientData));
      
      // Initialize socket connection
      if (token) {
        chatService.initializeSocket(token, clientData.client_id);
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    const clientId = client?.client_id;
    try {
      await authService.logout();
      chatService.disconnect();
      setClient(null);
      setIsAuthenticated(false);
      setHasCompletedProfile(false);
      clearPendingVerificationEmail();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Drop browser-local membership UI state so the next login cannot inherit it.
      clearMembershipStatus(clientId);
      sessionStorage.removeItem('cwy-esim-sale');
    }
  };

  const refreshClient = async () => {
    try {
      const clientData = await authService.getCurrentClient();
      setClient(clientData);
      setHasCompletedProfile(deriveProfileComplete(clientData));
    } catch (error) {
      console.error('Refresh client error:', error);
    }
  };

  const completeProfile = async (phone: string, country: string, state: string) => {
    const { client: updatedClient, hasCompletedProfile: complete } = await authService.updateProfile({
      phone,
      address: {
        country,
        state_province: state,
      },
    });
    setClient(updatedClient);
    setHasCompletedProfile(complete);
  };

  return (
    <AuthContext.Provider
      value={{
        client,
        isAuthenticated,
        isLoading,
        login,
        signUp,
        signInWithGoogle,
        logout,
        refreshClient,
        pendingVerificationEmail,
        setPendingVerificationEmail,
        clearPendingVerificationEmail,
        verifyOtpAndLogin,
        verifyMagicLinkAndLogin,
        hasCompletedProfile,
        setHasCompletedProfile,
        pendingProfileUser,
        setPendingProfileUser,
        clearPendingProfileUser,
        completeProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
